/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */

import {MutableRefObject, useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {GenericObject} from '@ion/core/src/types/entities';
import {endOfDay, getDateFromToday, startOfDay} from '@ion/core/src/utils/DateUtil';
import {useIsPhone} from '@ion/dls/src/hooks/useMedia';
import {LIVE_HISTORY_LAYER_ID} from '@ion/dls/src/maps/constants';
import {BoundaryMode, DateSelectorValues, TrackingMode} from '@ion/dls/src/types/types';
import {
  applyAssetHistoryDateFilter,
  clearFilters,
  setBoundaryDrawingTool,
  setBoundaryPanelOpen,
  setIsMobileMapView
} from '@/store/actions/reports';
import {
  applyBoundaryFilter,
  cancelLiveHistory,
  clearSelectedFilters,
  exitFindNearby,
  runLiveHistory,
  setBoundaryFilter,
  setHistoryRings,
  setLiveHistoryFetching,
  setLiveHistoryHideNonSelectedAssets,
  setLiveHistoryInputEvents,
  setLiveHistorySearchTime,
  setMapWidgetSelected,
  setPersistLiveHistory,
  setSelectedFiltersActive,
  setTrackingMapLayers,
  setTrackingMode,
  viewAssetHistory
} from '@/store/actions/tracking';
import {
  reportsBoundaryPanelOpenSelector,
  selectedBoundaryToolSelector
} from '@/store/selectors/reports-selectors';
import {
  boundaryFilterSelector,
  boundaryModeSelector,
  liveHistoryAssetSearchListSelector,
  liveHistoryRingsSelector,
  mapLayerSelector,
  trackingModeSelector
} from '@/store/selectors/tracking-selectors';
import {useApplyHistoryTimeMode} from '../history/useApplyHistoryTimeMode';
import {useSelectAssetAndResets} from '../sidesheet/useSelectAssetAndResets';

interface UseBoundaryFilterParams {
  closeMobileMenu?: Function;
  moreFiltersRef?: MutableRefObject<GenericObject>;
  vicinitySearchOnTracking?: boolean;
};

/**
 * The useBoundaryFilter hook is used to manage the boundary filter functionality.
 *
 * @param {Object} params - Optional parameters.
 * @param {Function} params.closeMobileMenu - A function to close the mobile menu.
 * @param {MutableRefObject<GenericObject>} params.moreFiltersRef - A mutable ref
 *   object that points to a GenericObject with a handleClose function used to
 *   close additional filters.
 *
 * @returns {Object} An object containing the following boundary filter actions:
 * - onApplyBoundary: A function to clear the current boundary filter and hide the boundary panel.
 * - onClickBoundaryList: A function to toggle the visibility of the boundary drawing tool and open the
 *   boundary panel with the provided boundary tool. If on a mobile device, it sets the map view to mobile mode.
 * - toggleShowBoundaryPanel: A function to toggle the open state of the boundary panel.
 * - onApplyBoundary: A function to apply the specified boundary filter rings and close the boundary panel.
 *
 * @example
 *
 *   import { useBoundaryFilter } from './useBoundaryFilter';
 *
 *   const MyComponent = () => {
 *     const {
 *       onApplyBoundary,
 *       onClickBoundaryList,
 *       toggleShowBoundaryPanel,
 *       onApplyBoundary
 *     } = useBoundaryFilter({
 *       closeMobileMenu: myCloseMobileMenuFunction,
 *       moreFiltersRef: myMoreFiltersRef
 *     });
 *
 *     // Your component logic and JSX here...
 *   };
 */
const useBoundaryFilter = ({
  closeMobileMenu,
  moreFiltersRef,
  vicinitySearchOnTracking = false
}: UseBoundaryFilterParams) => {
  const dispatch             = useDispatch();
  const isPhone              = useIsPhone();
  const boundaryPanelOpen    = useSelector(reportsBoundaryPanelOpenSelector);
  const boundary             = useSelector(boundaryFilterSelector);
  const selectedBoundaryTool = useSelector(selectedBoundaryToolSelector);
  const layers               = useSelector(mapLayerSelector);
  const [, resetTracking]    = useSelectAssetAndResets();
  const trackingMode         = useSelector(trackingModeSelector);
  const boundaryMode         = useSelector(boundaryModeSelector);
  const historyRings         = useSelector(liveHistoryRingsSelector);
  const assetsSearchList     = useSelector(liveHistoryAssetSearchListSelector);

  const toggleShowBoundaryPanel = useCallback((isOpen = false, mode?: BoundaryMode) => {
    dispatch(setBoundaryPanelOpen(isOpen, mode));
    moreFiltersRef?.current?.handleClose?.(); // Make sure to define moreFiltersRef in the calling component
  }, [moreFiltersRef, boundaryPanelOpen]);

  const onClickBoundaryList = useCallback((selectedBoundaryTool = null, fromUnMount = false) => {
    dispatch(setBoundaryDrawingTool(selectedBoundaryTool));
    toggleShowBoundaryPanel(!!selectedBoundaryTool, fromUnMount ? undefined : BoundaryMode.Boundary);
    closeMobileMenu?.();
    isPhone && dispatch(setIsMobileMapView(!!selectedBoundaryTool));
  }, [closeMobileMenu, isPhone, toggleShowBoundaryPanel]);

  const applyHistoryTimeMode = useApplyHistoryTimeMode();

  // in the current theme a vicinity search over past hours is a History
  // report like any other, so the Time Mode control is moved to History
  // over the hours searched ...
  const applyVicinityHistory = useCallback(({fromDate, toDate}) => {
    applyHistoryTimeMode({preset: DateSelectorValues.Custom, from: fromDate, to: toDate});
  }, [applyHistoryTimeMode]);

  const onApplyBoundary = useCallback((rings = [], onlyFilter: boolean = false, time) => {
    resetTracking();
    dispatch(exitFindNearby() as any);
    toggleShowBoundaryPanel();
    dispatch(applyBoundaryFilter(rings, onlyFilter) as any);
    !rings.length && dispatch(setBoundaryDrawingTool());
    if (boundaryMode === BoundaryMode.Vicinity || historyRings?.length) {
      if (vicinitySearchOnTracking) {
        dispatch(setHistoryRings(rings) as any);
        if (rings.length && (time || (assetsSearchList?.length && rings.length))) {
          time && dispatch(setLiveHistorySearchTime(time) as any);
          dispatch(setLiveHistoryHideNonSelectedAssets(true));
          dispatch(setTrackingMapLayers([...layers, LIVE_HISTORY_LAYER_ID]));
          const timeout = setTimeout(() => {
            dispatch(runLiveHistory(rings as any));
            clearTimeout(timeout);
          }, 350);
        } else if (!assetsSearchList?.length) {
          const filteredLayers = layers.filter((layer: string) => layer !== LIVE_HISTORY_LAYER_ID);
          dispatch(setTrackingMapLayers(filteredLayers));
          dispatch(setLiveHistoryHideNonSelectedAssets(false));
          dispatch(setLiveHistoryInputEvents([]));
          dispatch(cancelLiveHistory());
          dispatch(setLiveHistoryFetching(false));
        }
        dispatch(setMapWidgetSelected(null));
        dispatch(setPersistLiveHistory(false));
      } else {
        if (trackingMode === TrackingMode.Live && time) {
          const dates = getDateFromToday(time);
          dispatch(clearFilters());
          dispatch(clearSelectedFilters());
          dispatch(setSelectedFiltersActive(true) as any);
          const timeout = setTimeout(() => {
            dispatch(setBoundaryFilter(rings));
            dispatch(applyAssetHistoryDateFilter({
              fromDate      : dates?.fromDate,
              toDate        : dates?.toDate,
              selectedRadio : DateSelectorValues.Custom
            }) as any);
            dispatch(setTrackingMode(TrackingMode.History) as any);
            applyVicinityHistory(dates);
            clearTimeout(timeout);
          }, 350);
        } else if (trackingMode === TrackingMode.History && time) {
          const dates  = getDateFromToday(time);
          dispatch(applyAssetHistoryDateFilter({
            fromDate      : dates?.fromDate,
            toDate        : dates?.toDate,
            selectedRadio : DateSelectorValues.Custom
          }) as any);
          applyVicinityHistory(dates);
        }
      }
    }
  }, [
    toggleShowBoundaryPanel,
    applyVicinityHistory,
    trackingMode,
    layers,
    boundaryMode,
    historyRings,
    assetsSearchList
  ]);

  return {
    boundary,
    boundaryMode,
    boundaryPanelOpen,
    selectedBoundaryTool,
    onApplyBoundary,
    onClickBoundaryList,
    toggleShowBoundaryPanel
  };
};

export default useBoundaryFilter;
